import { Badge, Card, Row, Spin } from "antd";
import React, { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { ModulePermission, Permissions, Resource } from "../../actions/api";
import { selectPermissions, selectResources, selectUser } from "../../actions/userSlice";
import { AppSupportBar } from "../../components/layout/AppSupportBar";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Routes } from "../routes";
import styles from './HomePage.module.scss';
import './HomePage.scss'; // TODO: remove when query builder eolWrapper ribbon is removed

import { ReactComponent as CohortTrackerIcon } from './icons/svg/Cohort-Tracker.svg';
import { ReactComponent as HesDashboardIcon } from './icons/svg/HES.svg';
import { ReactComponent as HruIcon } from './icons/svg/HRU.svg';
import { ReactComponent as PathwayViewerIcon } from './icons/svg/PWV.svg';
import { ReactComponent as QofDashboardsIcon } from './icons/svg/QOF.svg';
import { ReactComponent as QueryBuilderV2Icon } from './icons/svg/QB-Icon.svg';
import { ReactComponent as RetinalMarketTrendsIcon } from './icons/svg/Retinal-Market-Trends.svg';
import { ReactComponent as StoryboardIcon } from './icons/svg/Storyboard.svg';
import { ReactComponent as BespokeDashboardsIcon } from './icons/svg/bespoke.svg';
import { ReactComponent as SimulationsIcon } from './icons/svg/custom.svg';
import { ReactComponent as PrescribingDashboardIcon } from './icons/svg/prescribing.svg';

import classNames from "classnames";
import { CopyrightNotice } from "../../components/common/CopyrightNotice";
import { selectWorkbooks, setWorkbook } from "../../containers/Storyboard/storyboardSlice";
import SettingsContext from "../../reducers/SettingsContext";
import { STORYBOARD_HOME_PAGE } from "../../reducers/settings";
import { Workbook } from "../../types/Storyboard";
import { QB_TITLE } from "../QueryBuilderPage";

type HomepagePanelProps = {
    title: string,
    route: string,
    Icon: any,
    module?: ModulePermission
    disableOnNoPermission?: boolean
    hideOnDemo?: boolean
    eolWrapper?: boolean
}

function isReactApplicationRoute(route: string) {
    const routes = Object.values(Routes)
    return routes.find(applicationRoute => route.startsWith(applicationRoute)) !== undefined
}

const PanelLink = ({
                       route,
                       disableOnNoPermission,
                       children
                   }: Pick<HomepagePanelProps, 'route' | 'disableOnNoPermission'> & { children: JSX.Element }) => {
    if (disableOnNoPermission) {
        return <div className={styles.panelLink}>
            {children}
        </div>
    }

    if (isReactApplicationRoute(route)) {
        return <Link className={styles.panelLink} to={route}>
            {children}
        </Link>
    }

    return <a className={styles.panelLink} href={route}>
        {children}
    </a>
}

const HomepagePanel = ({title, route, Icon, module, disableOnNoPermission, hideOnDemo, eolWrapper}: HomepagePanelProps) => {
    const permissions = useAppSelector(selectPermissions) as Permissions
    const user = useAppSelector(selectUser)

    const isEnabled = module === undefined || permissions.modulePermissions.includes(module)
    const showAsDisabled = !isEnabled && disableOnNoPermission

    if (!isEnabled && !disableOnNoPermission) {
        return null
    }

    if (user?.company.isDemo && hideOnDemo) {
        return null
    }

    const panel = <PanelLink route={route} disableOnNoPermission={showAsDisabled}>
        <Card
            hoverable={disableOnNoPermission === undefined ? true : !showAsDisabled}
            className={classNames(styles.panel, { [styles.panelDisabled]: showAsDisabled })}
            cover={<Icon className={styles.panelIcon} />}
        >
            <Card.Meta
                className={styles.titleContainer}
                title={<p className={styles.title}>{title}</p>} />
        </Card>
    </PanelLink>

    if (eolWrapper) {
        return <Badge.Ribbon text="&nbsp;&nbsp;&nbsp;End of Life: October&nbsp;&nbsp;&nbsp;" color="red" style={{
            top: '35px',
        }}
        >
            {panel}
        </Badge.Ribbon>
    } else {
        return panel
    }

}

const HESDashboard = () => <HomepagePanel
    title="HES Dashboard"
    route="diseasesArea.do"
    module={ModulePermission.HES_DASHBOARD}
    Icon={HesDashboardIcon}
    hideOnDemo
/>

const PrescribingDashboard = () => <HomepagePanel
    title="Prescribing Dashboard"
    route="dashboardPre.do"
    module={ModulePermission.PRESCRIBING_DASHBOARDS}
    Icon={PrescribingDashboardIcon}
    hideOnDemo
/>

const QOFDashboard = () => <HomepagePanel
    title="QOF Dashboard"
    route="dashboardQof.do"
    module={ModulePermission.QOF_DASHBOARD}
    Icon={QofDashboardsIcon}
    hideOnDemo
/>

const BespokeProceduralDashboards = () => <HomepagePanel
    title="Bespoke Procedural Dashboards"
    route="dashboardProcedure.do"
    module={ModulePermission.PROCEDURE_AREA_DASHBOARD}
    Icon={BespokeDashboardsIcon}
    hideOnDemo
/>

const HRU = () => <HomepagePanel
    title="Healthcare Resource Utilisation"
    route={Routes.MyStudiesHRU}
    Icon={HruIcon}
    module={ModulePermission.COHORT_HRU}
/>

const PathwayViewer = () => <HomepagePanel
    title="Pathway Viewer"
    route={Routes.MyStudiesPWV}
    module={ModulePermission.COHORT_PWV}
    Icon={PathwayViewerIcon}
/>

const CohortBuilder = () => <HomepagePanel
    title="Cohort Builder"
    route={Routes.Projects}
    module={ModulePermission.COHORT_BUILDER}
    Icon={CohortTrackerIcon}
/>

const RetinaMarketTrends = () => <HomepagePanel
    title="Retina Market Trends"
    route={Routes.RetinaMarketTrends}
    module={ModulePermission.STORYBOARD_RETINA_MARKET_TRENDS}
    Icon={RetinalMarketTrendsIcon}
/>

const VestrumPracticePerformanceTracker = () => <HomepagePanel
    title="Vestrum Practice Performance Tracker"
    route={Routes.VestrumPracticePerformanceTracker}
    module={ModulePermission.STORYBOARD_VESTRUM_PRACTICE_PERFORMANCE_TRACKER}
    Icon={RetinalMarketTrendsIcon}
/>

const QueryBuilderV2 = () => <HomepagePanel
    title={QB_TITLE}
    route={Routes.QueryBuilderV2}
    module={ModulePermission.QUERY_BUILDER_V2}
    Icon={QueryBuilderV2Icon}
/>

const Storyboard = ({showStoryboardOnHomepage, workbooks}: {
    showStoryboardOnHomepage: boolean,
    workbooks: Workbook[] | undefined
}) => {
    if (!workbooks) {
        return null
    }

    if (showStoryboardOnHomepage) {
        return <>{workbooks.map(i => {
            return <HomepagePanel
                key={i.workbookId}
                title={i.name}
                route={`${Routes.Storyboard}/${i.workbookId}`}
                module={ModulePermission.STORYBOARD}
                Icon={StoryboardIcon}
            />
        })}</>
    } else {
        return <HomepagePanel
            key="storyboard"
            title="Storyboard"
            route={Routes.Storyboard}
            module={ModulePermission.STORYBOARD}
            Icon={StoryboardIcon}
        />
    }
}

const Simulations = ({resources}: {resources: Resource[]}) => {
    const simulationsParentResource = resources.find(resource => resource.code === ModulePermission.SIMULATIONS)

    if (simulationsParentResource === undefined) {
        return null
    }

    return <>
        {
            simulationsParentResource.children.map(simulationsResource => {
                return <HomepagePanel
                    key={simulationsResource.code}
                    title={simulationsResource.label}
                    route={`${Routes.Simulations}/${simulationsResource.code.toLowerCase()}`}
                    module={ModulePermission.SIMULATIONS}
                    Icon={SimulationsIcon}
                />
            })
        }
    </>
}

export const HomePage = () => {
    const dispatch = useAppDispatch()
    const permissions = useAppSelector(selectPermissions)
    const resources = useAppSelector(selectResources)
    const user = useAppSelector(selectUser)
    const settingValues = useContext(SettingsContext)
    const workbooks = useAppSelector(selectWorkbooks)
    dispatch(setWorkbook(undefined))

    useEffect(() => {
        document.body.classList.add(styles.cover)
        return () => {
            document.body.classList.remove(styles.cover)
        }
    })

    return <div>
        <AppSupportBar/>
        <div className={styles.homePageContainer}>
            <div className={styles.panelsContainer}>
                {resources === undefined || permissions === undefined || user === undefined ? <Spin size="large"/>
                    : <Row gutter={[0, 24]} justify="center">
                        <HESDashboard/>
                        <PrescribingDashboard/>
                        <QOFDashboard/>
                        <BespokeProceduralDashboards/>
                        <HRU/>
                        <PathwayViewer/>
                        <QueryBuilderV2/>
                        <CohortBuilder/>
                        <Storyboard showStoryboardOnHomepage={settingValues[STORYBOARD_HOME_PAGE]} workbooks={workbooks}/>
                        <Simulations resources={resources.resources}/>
                        <RetinaMarketTrends/>
                        <VestrumPracticePerformanceTracker/>
                    </Row>
                }
            </div>
        </div>
        <CopyrightNotice/>
    </div>
}